import { Link } from 'react-router-dom';
import { ArrowRight, Brain, BarChart3, ShieldCheck, FileText, Sparkles, CheckCircle2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import BrandLogo from '../components/BrandLogo';
import { useAuth } from '../lib/auth';

export default function Landing() {
  const { user } = useAuth();
  const cta = user
    ? (user.show_welcome_after_login === false ? '/assessment' : '/welcome')
    : '/auth';

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0 bg-grid-fine opacity-60" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#002FA7]/5 rounded-full blur-3xl" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-6 border border-zinc-300 px-3 py-1.5" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 bg-[#002FA7] rounded-full pulse-dot" />
              AI Readiness for SMEs · STArtIn AI
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-zinc-950 leading-[0.95]">
              Is your business
              <br />
              <span className="text-[#002FA7]">AI-ready?</span>
            </h1>
            <p className="mt-7 text-lg text-zinc-600 max-w-xl leading-relaxed">
              A structured assessment platform for SMEs. Define goals, evaluate data, audit your stack —
              and receive an executive-grade AI readiness report in minutes.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to={cta} className="group bg-[#002FA7] text-white px-7 py-3.5 hover:bg-[#00227A] transition-colors inline-flex items-center gap-2 text-sm font-medium" data-testid="hero-cta-primary">
                Get Your Free Synopsis <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/auth" className="text-sm text-zinc-700 hover:text-zinc-950 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-950 transition-colors" data-testid="hero-cta-secondary">
                Already have an account? Sign in
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { v: '4', l: 'Sections' },
                { v: 'AI', l: 'Powered' },
                { v: 'DOCX', l: 'Export' },
              ].map((s, i) => (
                <div key={i} className="border-l border-zinc-300 pl-3">
                  <div className="font-display text-2xl font-bold text-zinc-950">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square max-w-md ml-auto">
              <img
                src="https://images.pexels.com/photos/17485706/pexels-photo-17485706.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="AI network"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#002FA7]/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 glass-card p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Live Output</div>
                <div className="font-display text-xl font-bold text-zinc-950 mt-1">Moderately Ready</div>
                <div className="text-xs text-zinc-600 mt-1">73% data maturity · 2 actionable goals identified</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">/ 01 — Capabilities</div>
            <h2 className="font-display text-4xl font-bold tracking-tight text-zinc-950 leading-tight">
              Built for SMEs.<br/>Designed for clarity.
            </h2>
            <p className="text-zinc-600 mt-5 leading-relaxed">
              Every prompt, every score, every recommendation is engineered to translate strategic intent into AI-actionable plans.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-zinc-200">
            {[
              { i: Brain, t: 'AI Goal Drilldown', d: 'Vague strategic goals get broken into actionable, measurable AI-solvable statements.' },
              { i: BarChart3, t: 'Data Audit', d: 'Map internal & external data sources required to power each goal.' },
              { i: ShieldCheck, t: 'Governance Check', d: 'Surface regulatory, ethical, and security gaps before they block deployment.' },
              { i: FileText, t: 'Executive Report', d: 'Download a fully formatted Word document ready for the boardroom.' },
            ].map((f, i) => (
              <div key={i} className="bg-white p-8 hover:bg-zinc-50 transition-colors group" data-testid={`feature-${i}`}>
                <f.i className="w-6 h-6 text-[#002FA7] mb-5" strokeWidth={1.5} />
                <h3 className="font-display font-semibold text-lg text-zinc-950">{f.t}</h3>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">/ 02 — Method</div>
          <h2 className="font-display text-4xl font-bold tracking-tight text-zinc-950 mb-12 max-w-2xl">A four-step assessment, refined for SMEs.</h2>
          <div className="grid md:grid-cols-4 gap-px bg-zinc-200">
            {[
              { n: '01', t: 'Company Information', d: 'Industry, size, customer segments and operations.' },
              { n: '02', t: 'Goals & Challenges', d: 'Up to 5 prioritised goals refined through AI drilldown.' },
              { n: '03', t: 'Data Sources', d: 'Map internal systems & external data requirements.' },
              { n: '04', t: 'Technology Stack', d: 'Software, workloads, compliance, ethics.' },
            ].map((s, i) => (
              <div key={i} className="bg-zinc-50 p-8">
                <div className="font-mono text-xs text-[#002FA7]">{s.n}</div>
                <h3 className="font-display font-semibold text-lg text-zinc-950 mt-3">{s.t}</h3>
                <p className="text-sm text-zinc-600 mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="bg-zinc-950 text-white p-12 lg:p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-10" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Sparkles className="w-6 h-6 text-[#FF3B30] mb-5" strokeWidth={1.5} />
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Start your AI<br/>readiness assessment.
              </h2>
              <p className="text-zinc-400 mt-5 max-w-md">Free Synopsis. Confidential. ~15 minutes. Take the first step toward an AI-augmented business.</p>
            </div>
            <div className="flex flex-col gap-3">
              {['Multi-step guided form', 'AI-powered goal refinement', 'Downloadable executive report', 'Save drafts & revisit anytime'].map((b, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 text-[#FF3B30]" strokeWidth={1.5} />{b}
                </div>
              ))}
              <Link to={cta} className="mt-6 bg-white text-zinc-950 px-7 py-4 hover:bg-zinc-100 transition-colors inline-flex items-center gap-2 text-sm font-medium self-start" data-testid="cta-bottom">
                Begin Assessment <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-500">
          <div className="flex items-center gap-4"><Link to="/" aria-label="STArtIn AI home"><BrandLogo className="w-14 h-auto" /></Link><span>© {new Date().getFullYear()} STArtIn AI — SMEs Thinking Artificial Intelligence</span></div>
          <div className="font-mono">v1.0 / preview</div>
        </div>
      </footer>
    </div>
  );
}
